import type { BallAttack } from '../../../data/full/sportif';
import { angleOf } from '../../../shared/angle';
import { distanceSquared } from '../../../shared/vec';
import type { AttackModule } from '../../attacks/types';
import { pullTrigger } from '../../attacks/trigger';
import { hurtEnemy } from '../../effects';
import { gaugeValue } from '../../gauges';
import { touchesObstacle } from '../../obstacles';
import type { EnemyState, PlayerState, PropState, SimState } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import { flyBack, ownerOf } from '../../props';

const BALL = 'ball';

export const ball: AttackModule<BallAttack> = {
  reads: ['attackCooldownMul', 'damageMul', 'pierceAdd', 'projectileSpeedMul'],

  ammunition: true,

  step(ctx, player, attack, input) {
    const { state } = ctx;
    const owned = ballOf(state, player);
    const atFeet = owned === undefined || owned.phase === 'held';
    if (!pullTrigger(player, input.fire && atFeet, attack.cooldownTicks)) {
      return;
    }
    const kicked = owned ?? newBall(state, player);
    const max = ctx.content.classes.get(player.classId)?.gauge?.max;
    const full = max !== undefined && gaugeValue(player) >= max;
    kicked.phase = 'flying';
    kicked.x = kicked.prevX = player.x;
    kicked.y = kicked.prevY = player.y;
    const speed = statValue(player, 'projectileSpeedMul', attack.speed);
    kicked.vx = player.aim.x * speed;
    kicked.vy = player.aim.y * speed;
    kicked.damage =
      statValue(player, 'damageMul', attack.damage) * (full ? attack.elan.damageMul : 1);
    kicked.ticksLeft = attack.rangeTicks;
    kicked.hitsLeft =
      statValue(player, 'pierceAdd', attack.bounces) + (full ? attack.elan.bounces : 0);
    kicked.hitIds = [];
    if (full) {
      kicked.boosted = true;
    } else {
      delete kicked.boosted;
    }
    state.events.push({
      type: 'playerFired',
      playerId: player.id,
      x: player.x,
      y: player.y,
      angle: angleOf(player.aim),
    });
  },

  update(ctx) {
    const { state } = ctx;
    for (const prop of state.props ?? []) {
      if (prop.kind !== BALL) {
        continue;
      }
      const owner = ownerOf(state, prop);
      const attack = attackOf(ctx, owner);
      switch (prop.phase) {
        case 'flying':
          fly(ctx, prop, owner, attack);
          break;
        case 'bouncing':
          bounce(ctx, prop, owner, attack);
          break;
        case 'returning':
          flyBack(prop, owner, statValue(owner, 'projectileSpeedMul', attack.returnSpeed));
          break;
        case 'held':
        case 'grounded':
          break;
      }
    }
  },

  reach(attack, player) {
    return statValue(player, 'projectileSpeedMul', attack.speed) * attack.rangeTicks;
  },
};

export function ballOf(state: SimState, player: Pick<PlayerState, 'id'>): PropState | undefined {
  return state.props?.find((prop) => prop.kind === BALL && prop.playerId === player.id);
}

function newBall(state: SimState, player: PlayerState): PropState {
  const made: PropState = {
    id: state.nextEntityId,
    kind: BALL,
    playerId: player.id,
    phase: 'held',
    x: player.x,
    y: player.y,
    prevX: player.x,
    prevY: player.y,
    vx: 0,
    vy: 0,
    damage: 0,
    ticksLeft: 0,
    hitsLeft: 0,
  };
  state.nextEntityId += 1;
  (state.props ??= []).push(made);
  return made;
}

function attackOf(ctx: StepContext, owner: PlayerState): BallAttack {
  const attack = ctx.content.classes.get(owner.classId)?.attack;
  if (attack?.kind !== 'ball') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" kicks no ball`);
  }
  return attack;
}

function fly(ctx: StepContext, prop: PropState, owner: PlayerState, attack: BallAttack): void {
  const { state } = ctx;
  const { width, height } = state.arena;
  prop.ticksLeft -= 1;
  const steps = stepsOf(prop, attack);
  for (let step = 0; step < steps; step++) {
    prop.x += prop.vx / steps;
    prop.y += prop.vy / steps;
    const met = nearestInReach(state, prop, attack.radius);
    if (met !== undefined) {
      strike(ctx, prop, owner, attack, met);
      return;
    }
    const out = prop.x < 0 || prop.x > width || prop.y < 0 || prop.y > height;
    if (out || blocked(ctx, prop, attack)) {
      rollHome(prop);
      return;
    }
  }
  if (prop.ticksLeft <= 0) {
    rollHome(prop);
  }
}

// Steps no longer than its width, so that a fast ball never skips a bad vibe or an obstacle.
function stepsOf(prop: PropState, attack: BallAttack): number {
  const speed = Math.sqrt(prop.vx * prop.vx + prop.vy * prop.vy);
  return Math.max(1, Math.ceil(speed / (2 * attack.radius)));
}

function blocked(ctx: StepContext, prop: PropState, attack: BallAttack): boolean {
  return touchesObstacle({ x: prop.x, y: prop.y, radius: attack.radius }, ctx.obstacles);
}

function nearestInReach(state: SimState, prop: PropState, radius: number): EnemyState | undefined {
  let met: EnemyState | undefined;
  for (const enemy of state.enemies) {
    const reach = radius + enemy.radius;
    if (
      enemy.hp > 0 &&
      distanceSquared(enemy, prop) <= reach * reach &&
      (met === undefined || distanceSquared(enemy, prop) < distanceSquared(met, prop))
    ) {
      met = enemy;
    }
  }
  return met;
}

function bounce(ctx: StepContext, prop: PropState, owner: PlayerState, attack: BallAttack): void {
  const target = ctx.state.enemies.find((enemy) => enemy.id === prop.targetId && enemy.hp > 0);
  prop.ticksLeft -= 1;
  if (target === undefined || prop.ticksLeft <= 0) {
    rollHome(prop);
    return;
  }
  const speed = statValue(owner, 'projectileSpeedMul', attack.speed);
  const dx = target.x - prop.x;
  const dy = target.y - prop.y;
  const distance = Math.sqrt(dx * dx + dy * dy);
  if (distance <= speed + target.radius) {
    prop.x = target.x;
    prop.y = target.y;
    strike(ctx, prop, owner, attack, target);
    return;
  }
  prop.vx = (dx / distance) * speed;
  prop.vy = (dy / distance) * speed;
  const steps = stepsOf(prop, attack);
  for (let step = 0; step < steps; step++) {
    prop.x += prop.vx / steps;
    prop.y += prop.vy / steps;
    if (blocked(ctx, prop, attack)) {
      rollHome(prop);
      return;
    }
  }
}

function strike(
  ctx: StepContext,
  prop: PropState,
  owner: PlayerState,
  attack: BallAttack,
  enemy: EnemyState,
): void {
  const { state } = ctx;
  hurtEnemy(state, enemy, prop.damage, prop.playerId, {
    x: prop.prevX,
    y: prop.prevY,
  });
  const hitIds = (prop.hitIds ??= []);
  hitIds.push(enemy.id);
  prop.hitsLeft -= 1;
  const next = prop.hitsLeft > 0 ? nearestUnhit(state, prop, attack.range) : undefined;
  if (next === undefined) {
    rollHome(prop);
    return;
  }
  const speed = statValue(owner, 'projectileSpeedMul', attack.speed);
  prop.phase = 'bouncing';
  prop.targetId = next.id;
  prop.ticksLeft = Math.ceil((2 * attack.range) / speed);
}

function nearestUnhit(state: SimState, prop: PropState, range: number): EnemyState | undefined {
  let nearest: EnemyState | undefined;
  let best = range * range;
  for (const enemy of state.enemies) {
    if (enemy.hp <= 0 || prop.hitIds?.includes(enemy.id) === true) {
      continue;
    }
    const distance = distanceSquared(enemy, prop);
    if (distance <= best) {
      nearest = enemy;
      best = distance;
    }
  }
  return nearest;
}

function rollHome(prop: PropState): void {
  prop.phase = 'returning';
  delete prop.targetId;
  delete prop.hitIds;
}
